import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Award,
    Briefcase,
    GraduationCap,
    Trophy,
    BookMarked,
    Users as UsersIcon,
    Building2,
    Plus,
    Trash2,
    X,
} from "lucide-react";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import RepublishBanner, { notifyPendingRepublishChanged } from "@/components/RepublishBanner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const KIND_META = {
    education: { label: "Education", icon: GraduationCap, fields: [
        { k: "degree", label: "Degree", required: true },
        { k: "institution", label: "Institution", required: true },
        { k: "year", label: "Year", type: "number" },
    ]},
    experience: { label: "Experience", icon: Briefcase, fields: [
        { k: "role", label: "Role/Title", required: true },
        { k: "organization", label: "Organization", required: true },
        { k: "from_year", label: "From year", type: "number" },
        { k: "to_year", label: "To year", type: "number" },
    ]},
    achievement: { label: "Achievements", icon: Trophy, fields: [
        { k: "title", label: "Title", required: true },
        { k: "year", label: "Year", type: "number" },
        { k: "description", label: "Description", multi: true },
    ]},
    award: { label: "Awards", icon: Award, fields: [
        { k: "title", label: "Title", required: true },
        { k: "awarded_by", label: "Awarded by" },
        { k: "year", label: "Year", type: "number" },
    ]},
    publication: { label: "Publications", icon: BookMarked, fields: [
        { k: "title", label: "Title", required: true },
        { k: "journal", label: "Journal / Conference" },
        { k: "year", label: "Year", type: "number" },
        { k: "url", label: "URL" },
    ]},
    membership: { label: "Memberships", icon: UsersIcon, fields: [
        { k: "organization", label: "Organization", required: true },
        { k: "role", label: "Role (e.g. Fellow, Member)" },
        { k: "since_year", label: "Since year", type: "number" },
    ]},
    hospital_affiliation: { label: "Hospital Affiliations", icon: Building2, fields: [
        { k: "hospital_name", label: "Hospital name", required: true },
        { k: "role", label: "Role" },
        { k: "city", label: "City" },
    ]},
};

const KINDS = Object.keys(KIND_META);

export default function Credentials() {
    const [doctor, setDoctor] = useState(null);
    const [summary, setSummary] = useState(null);
    const [active, setActive] = useState("education");
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null); // 'new' | item.id
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    async function loadSummary() {
        try {
            const [dr, s] = await Promise.all([api.get("/me/doctor"), api.get("/me/credentials")]);
            setDoctor(dr.data);
            setSummary(s.data);
        } catch (e) { toast.error(errMsg(e, "Failed")); }
    }
    async function loadKind(kind) {
        try {
            const r = await api.get(`/me/credentials/${kind}`);
            setItems(r.data || []);
        } catch (e) { toast.error(errMsg(e, "Failed")); }
    }
    useEffect(() => { loadSummary(); }, []);
    useEffect(() => { loadKind(active); setEditing(null); setForm({}); }, [active]);

    function startNew() { setEditing("new"); setForm({}); }
    function startEdit(it) { setEditing(it.id); setForm({ ...(it.payload || {}) }); }
    function cancel() { setEditing(null); setForm({}); }

    async function save() {
        const meta = KIND_META[active];
        for (const f of meta.fields) {
            if (f.required && !(form[f.k] || "").toString().trim()) {
                toast.error(`${f.label} is required`); return;
            }
        }
        // Coerce numeric fields
        const payload = { ...form };
        for (const f of meta.fields) {
            if (f.type === "number" && payload[f.k] != null && payload[f.k] !== "") {
                const n = parseInt(payload[f.k], 10);
                payload[f.k] = Number.isFinite(n) ? n : null;
            }
        }
        setSaving(true);
        try {
            if (editing === "new") {
                await api.post(`/me/credentials/${active}`, { payload });
            } else {
                await api.put(`/me/credentials/${active}/${editing}`, { payload });
            }
            toast.success(doctor?.profile_status === "published"
                ? "Saved. Admin has been notified; republish to make it live."
                : "Saved");
            await Promise.all([loadKind(active), loadSummary()]);
            cancel();
            notifyPendingRepublishChanged();
        } catch (e) { toast.error(errMsg(e, "Failed")); }
        finally { setSaving(false); }
    }

    async function del(it) {
        if (!window.confirm("Delete this item?")) return;
        try {
            await api.delete(`/me/credentials/${active}/${it.id}`);
            toast.success(doctor?.profile_status === "published"
                ? "Deleted. Admin notified; republish to make it live."
                : "Deleted");
            await Promise.all([loadKind(active), loadSummary()]);
            notifyPendingRepublishChanged();
        } catch (e) { toast.error(errMsg(e, "Failed")); }
    }

    const meta = KIND_META[active];
    const Icon = meta.icon;

    return (
        <DoctorShell doctor={doctor}>
            <div className="mb-6" data-testid="credentials-header">
                <h1 className="ddi-section-title">Credential Editors</h1>
                <p className="ddi-section-sub">Add or update your Education, Experience, Awards, Publications and more. Changes save immediately; admin gets a courtesy notification if your profile is already published.</p>
            </div>

            <RepublishBanner />

            {/* Tab strip */}
            <div className="flex gap-2 flex-wrap mb-5">
                {KINDS.map((k) => {
                    const KIcon = KIND_META[k].icon;
                    const cnt = summary?.counts?.[k] || 0;
                    return (
                        <button
                            key={k}
                            onClick={() => setActive(k)}
                            className={"flex items-center gap-2 px-4 py-2 rounded-xl border transition-all text-[13px] font-medium " + (active === k ? "bg-[#3A80F7] text-white border-[#3A80F7] shadow-sm" : "bg-white text-[#334155] border-[#E5E9F2] hover:border-[#3A80F7]")}
                            data-testid={`cred-tab-${k}`}
                        >
                            <KIcon size={14} />
                            {KIND_META[k].label}
                            <span className={"text-[11px] px-1.5 rounded-full " + (active === k ? "bg-white/25" : "bg-[#F5F7FB] text-[#64748B]")}>{cnt}</span>
                        </button>
                    );
                })}
            </div>

            {/* Editor */}
            {editing !== null && (
                <Card className="border-[#E5E9F2] ddi-shadow-sm mb-5" data-testid="cred-editor">
                    <CardHeader className="pb-3 flex flex-row items-center justify-between">
                        <CardTitle className="text-base">{editing === "new" ? `New ${meta.label.toLowerCase()}` : `Edit ${meta.label.toLowerCase()}`}</CardTitle>
                        <button onClick={cancel} aria-label="Cancel edit" className="text-[#64748B] hover:text-[#DC2626]"><X size={16} /></button>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {meta.fields.map((f) => (
                                <div key={f.k} className={f.multi ? "md:col-span-2" : ""}>
                                    <label className="text-[11px] font-semibold uppercase tracking-widest text-[#64748B] mb-1.5 block">
                                        {f.label} {f.required && <span className="text-[#DC2626]">*</span>}
                                    </label>
                                    {f.multi ? (
                                        <Textarea rows={3} value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} maxLength={2000} data-testid={`cred-field-${f.k}`} />
                                    ) : (
                                        <Input type={f.type || "text"} value={form[f.k] ?? ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} maxLength={200} data-testid={`cred-field-${f.k}`} />
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={cancel}>Cancel</Button>
                            <Button onClick={save} disabled={saving} className="bg-[#3A80F7] hover:bg-[#2D6BE0]" data-testid="cred-save-btn">
                                {saving ? "Saving…" : "Save"}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Items list */}
            <Card className="border-[#E5E9F2] ddi-shadow-sm">
                <CardHeader className="pb-3 flex flex-row items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2"><Icon size={18} className="text-[#3A80F7]" /> {meta.label}</CardTitle>
                    {editing === null && (
                        <Button onClick={startNew} className="bg-[#3A80F7] hover:bg-[#2D6BE0]" data-testid={`cred-add-${active}`}>
                            <Plus size={14} className="mr-1" /> Add {meta.label.toLowerCase()}
                        </Button>
                    )}
                </CardHeader>
                <CardContent>
                    {items.length === 0 ? (
                        <div className="py-8 text-center text-[13px] text-[#64748B]">
                            <Icon size={32} className="mx-auto text-[#94A3B8] mb-2" />
                            No {meta.label.toLowerCase()} yet.
                        </div>
                    ) : (
                        <ul className="divide-y divide-[#EEF1F6]">
                            {items.map((it) => {
                                const p = it.payload || {};
                                const primary = p.degree || p.role || p.title || p.organization || p.hospital_name || "—";
                                const secondary = p.institution || p.organization || p.awarded_by || p.journal || p.hospital_name || p.role || "";
                                const year = p.year || p.since_year || p.from_year || null;
                                return (
                                    <li key={it.id} className="py-3 flex items-start gap-3" data-testid={`cred-row-${it.id}`}>
                                        <div className="h-8 w-8 rounded-lg bg-[#EEF4FE] text-[#3A80F7] flex items-center justify-center shrink-0"><Icon size={14} /></div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[14px] font-semibold text-[#0F172A]">{primary}</div>
                                            <div className="text-[12px] text-[#64748B] mt-0.5">{secondary}{year ? ` · ${year}` : ""}</div>
                                            {p.description && <div className="text-[12px] text-[#64748B] mt-1 line-clamp-2">{p.description}</div>}
                                        </div>
                                        <button onClick={() => startEdit(it)} className="text-[12px] text-[#3A80F7] hover:underline" data-testid={`cred-edit-${it.id}`}>Edit</button>
                                        <button onClick={() => del(it)} className="text-[12px] text-[#DC2626] hover:underline" data-testid={`cred-del-${it.id}`}><Trash2 size={13} /></button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </CardContent>
            </Card>
        </DoctorShell>
    );
}
